/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InputLabel } from '@material-ui/core';
import TableGridWithFeatures from '../../../../../SharedModules/Table/TableGridWithFeatures';
import CustomSeparator from '../../../../../SharedModules/Breadcrumbs/BreadcrumbsComponent';
import DisplayErrorMessages from '../../../../../SharedModules/Errors/ErrorMessages';
import DisplaySuccessMessages from '../../../../../SharedModules/Errors/SuccessMessage';
import Spinner from '../../../../../SharedModules/Spinner/Spinner';

import {
  QUARTERLY_MRKT_HEAD,
  QUARTERLY_MRKT_SUP_HEAD,
} from '../../OnlineFixes/CreateStubInvoice/utils/tableHead';
import CustomGridCellRendererComponent from '../../../../../SharedModules/AgGridUtilities/CustomGridCellRenderercomponent';
import {
  navigateToNextCell,
  onCellFocused,
  tabToNextCell,
} from '../../../../../SharedModules/AgGridUtilities/suppressNavigation';

/* THis Component loads the details in the GRID of DPL */
const QuarterlyMarketingDataTableDetails = ({
  fullScreen,
  setFullScreen,
  filters,
  referenceData,
}) => {
  const childRef = useRef(null);
  const dispatch = useDispatch();
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [applyFilter, setApplyFilter] = useState(false);
  const [tableCount, setTableCount] = useState(0);
  const [errorMessages, setErrorMessages] = useState([]);
  const [successMessages, setSuccessMessages] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fullScreenView, setFullScreenView] = useState(false);
  const [isSup, setIsSup] = useState(false);
  // Lifecycle method will be called on Grid ready
  const beginQtr = useSelector(
    (state) => state?.sharedState?.QuarterlyMarketingDataReducer?.beginQtr
  );
  const endQtr = useSelector(
    (state) => state?.sharedState?.QuarterlyMarketingDataReducer?.endQtr
  );
  const rebateCD = useSelector(
    (state) => state?.sharedState?.QuarterlyMarketingDataReducer?.rebateCD
  );
  const gridData = useSelector(
    (state) => state?.sharedState?.QuarterlyMarketingDataReducer?.gridData
  );

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
    setTableCount(params.api.getDisplayedRowCount());
  }, []);

  useEffect(() => {
    resetFilters();

    setTableCount(gridApi && gridApi.getDisplayedRowCount());
  }, [gridApi, tableData]);

  const handleFullScreenViewClick = () => {
    setFullScreenView(!fullScreenView);
  };

  useEffect(() => {
    setLoading(true);

    setLoading(false);
    setTableData(gridData);
    if (gridData?.length > 0 && gridData[0]?.supplemental) {
      setIsSup(gridData[0]?.supplemental);
    }
  }, [gridData]);

  const resetFilters = () => {
    gridApi && gridApi.setFilterModel(null);
  };

  // Total Count on Table Cells Starts
  const sum = (property) => {
    if (Array.isArray(tableData)) {
      const data = tableData?.reduce((accumulator, object) => {
        return accumulator + object[property];
      }, 0);

      return data ? parseFloat(data)?.toFixed(2) : '0.00';
    }
    return 0;
  };

  const createData = (count) => {
    const result = [];
    for (let i = 0; i < count; i += 1) {
      result.push({
        isBottomRow: true,
        adjInvAmt: sum('adjInvAmt'),
        payments: sum('payments'),
        receiptsToAdjPct: sum('receiptsToAdjPct'),
        totalReimb: sum('totalReimb'),
        receiptsToReimbPct: sum('receiptsToReimbPct'),
        origTotReimb: sum('origTotReimb'),
        adjToReimb: sum('adjToReimb'),
        origInv: sum('origInv'),
        receiptsToOriginvPct: sum('receiptsToOriginvPct'),
        hCPCSCURRENTINVAMT: sum('hCPCSCURRENTINVAMT'),
        estHcpcsCodeReceipts: sum('estHcpcsCodeReceipts'),
        hCPCSCURRENTREIMB: sum('hCPCSCURRENTREIMB'),
        estToCurrHcpcsPct: sum('estToCurrHcpcsPct'),
      });
    }
    return result;
  };

  const pinnedBottomRowData = useMemo(() => {
    return createData(1, 'Bottom');
  }, [tableData]);

  return (
    <div className="tabs-container tBillPanel custom-check">
      {loading && <Spinner load={loading} />}
      <CustomSeparator
        parent1="System"
        parent2="Technical Window"
        parent3="Display Quarterly Marketing Data"
      />

      {successMessages && (
        <DisplaySuccessMessages successMessages={successMessages} />
      )}
      {errorMessages && <DisplayErrorMessages errorMessages={errorMessages} />}
      {!fullScreenView && (
        <div className="row">
          <fieldset className="custom-fieldset" style={{ width: '100%' }}>
            <div className="row col-12">
              <div className="col-5">
                <div className="p-0 mt-2 row">
                  <div className="mui-custom-form col-12 m-0">
                    <InputLabel>Rebate Program</InputLabel>
                  </div>
                </div>
              </div>
              <div className="col-5">
                <div className="p-0 mt-2 row">
                  <div className="mui-custom-form col-12 m-0">
                    <InputLabel>Quarter</InputLabel>
                  </div>
                </div>
              </div>
            </div>
            <div className="row col-12">
              <div
                className="col-2"
                style={{
                  backgroundColor: '#0000000a',
                  border: '1px solid white',
                  borderRadius: '5px',
                }}
              >
                <div className="p-0 mt-1 row">
                  <div className="mui-custom-form col-12 m-0">{rebateCD}</div>
                </div>
              </div>
              <div className="col-3" />
              <div
                className="col-2"
                style={{
                  backgroundColor: '#0000000a',
                  border: '1px solid white',
                  borderRadius: '5px',
                }}
              >
                <div className="p-0 mt-1 row">
                  <div className="mui-custom-form col-12 m-0">
                    {beginQtr}- {endQtr}
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      )}
      <TableGridWithFeatures
        data-testid="QMD-cbi-table"
        fullScreenView={fullScreenView}
        handleFullScreenViewClick={handleFullScreenViewClick}
        handleFullScreenView={handleFullScreenViewClick}
        gridHeight={fullScreenView ? '100vh' : 350}
        rowData={
          tableData?.map((item) => {
            item.receiptsToAdjPct = item.receiptsToAdjPct
              ? parseFloat(item.receiptsToAdjPct).toFixed(2)
              : '0.00';
            item.receiptsToOriginvPct = item.receiptsToOriginvPct
              ? parseFloat(item.receiptsToOriginvPct).toFixed(2)
              : '0.00';
            item.receiptsToReimbPct = item.receiptsToReimbPct
              ? parseFloat(item.receiptsToReimbPct).toFixed(2)
              : '0.00';
            return item;
          }) || []
        }
        columnDefs={
          isSup === true ? QUARTERLY_MRKT_SUP_HEAD : QUARTERLY_MRKT_HEAD
        }
        exportFile
        gridCall={onGridReady}
        singleClickEdit={false}
        fileName="Quarterly Marketing Data"
        frameworkComponents={{
          amountComponent: CustomGridCellRendererComponent,
        }}
        pinnedBottomRowData={pinnedBottomRowData}
        enterMovesDown
        suppressCellFocus
        isScrollOn
        defaultColWidth={150}
        onCellFocused={onCellFocused}
        navigateToNextCell={navigateToNextCell}
        tabToNextCell={tabToNextCell}
      />
    </div>
  );
};

export default QuarterlyMarketingDataTableDetails;
